import { NASAResponse, SolarData } from '../types';

/**
 * Fetch solar radiation data from NASA POWER API
 * @param latitude - Location latitude
 * @param longitude - Location longitude
 * @returns Solar data including monthly GHI values
 */
export async function fetchSolarData(
  latitude: number,
  longitude: number
): Promise<SolarData> {
  try {
    // NASA POWER API endpoint for monthly solar data
    const params = new URLSearchParams({
      parameters: 'ALLSKY_SFC_SW_DWN,T2M,PRECTOTCORR,RH2M', // GHI, Temperature, Precipitation, Humidity
      community: 'RE',
      longitude: longitude.toString(),
      latitude: latitude.toString(),
      format: 'JSON',
      start: '2020',
      end: '2023',
    });

    const url = `https://power.larc.nasa.gov/api/temporal/monthly/point?${params}`;
    
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🛰️  NASA POWER API REQUEST');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📍 Location:', `${latitude.toFixed(4)}°, ${longitude.toFixed(4)}°`);
    console.log('🔗 Endpoint:', url);
    console.log('📊 Requesting Parameters:');
    console.log('   • ALLSKY_SFC_SW_DWN (Global Horizontal Irradiance)');
    console.log('   • T2M (Temperature at 2 meters)');
    console.log('   • PRECTOTCORR (Precipitation corrected)');
    console.log('   • RH2M (Relative Humidity at 2 meters)');
    console.log('📅 Date Range: 2020-2023 (4-year average)');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`NASA API error: ${response.status}`);
    }

    const data: NASAResponse = await response.json();
    
    console.log('✅ NASA POWER API RESPONSE RECEIVED');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📦 Raw Response Structure:', {
      hasProperties: !!data.properties,
      hasParameter: !!data.properties?.parameter,
      parameterKeys: Object.keys(data.properties?.parameter || {}),
    });
    
    // Extract monthly GHI values (average of multiple years)
    const ghiData = data.properties.parameter.ALLSKY_SFC_SW_DWN;
    const tempData = data.properties.parameter.T2M || {};
    const precipData = data.properties.parameter.PRECTOTCORR || {};
    const humidityData = data.properties.parameter.RH2M || {};
    
    console.log('\n📊 RAW NASA DATA (Sample - First 3 months):');
    console.log('   GHI Data (ALLSKY_SFC_SW_DWN):', Object.entries(ghiData).slice(0, 3));
    console.log('   Temperature (T2M):', Object.entries(tempData).slice(0, 3));
    console.log('   Precipitation (PRECTOTCORR):', Object.entries(precipData).slice(0, 3));
    console.log('   Humidity (RH2M):', Object.entries(humidityData).slice(0, 3));
    
    // Calculate monthly averages
    const monthlyGHI = calculateMonthlyAverages(ghiData);
    const avgTemperature = calculateAverageValue(tempData, 25); // Default 25°C
    const avgPrecipitation = calculateAverageValue(precipData, 2.0); // Default 2.0 mm/day
    const avgHumidity = calculateAverageValue(humidityData, 60); // Default 60%
    
    console.log('\n📈 CALCULATED CLIMATE AVERAGES:');
    console.log('   🌡️  Average Temperature:', avgTemperature.toFixed(2), '°C');
    console.log('   💧 Average Precipitation:', avgPrecipitation.toFixed(2), 'mm/day');
    console.log('   💨 Average Humidity:', avgHumidity.toFixed(1), '%');
    console.log('   ☀️  Monthly GHI (12 months):', monthlyGHI.map(v => v.toFixed(2)));
    
    // Calculate annual GHI
    const annualGHI = monthlyGHI.reduce((sum, val) => sum + val, 0) * 30.42; // avg days per month
    
    console.log('   📊 Annual GHI:', annualGHI.toFixed(0), 'kWh/m²/year');
    
    // Calculate optimal tilt angle (rule of thumb: latitude ± 15°)
    const optimalTiltAngle = Math.abs(latitude);
    
    console.log('   📐 Optimal Tilt Angle:', optimalTiltAngle.toFixed(1), '°');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    
    return {
      monthlyGHI,
      annualGHI,
      avgTemperature,
      avgPrecipitation,
      avgHumidity,
      optimalTiltAngle,
    };
  } catch (error) {
    console.error('❌ Error fetching NASA POWER data:', error);
    throw new Error('Failed to fetch solar radiation data. Please check your internet connection and try again.');
  }
}

/**
 * Calculate monthly averages from multi-year data
 */
function calculateMonthlyAverages(data: { [key: string]: number }): number[] {
  const monthlyValues: { [month: number]: number[] } = {};
  
  // Group values by month
  Object.entries(data).forEach(([key, value]) => {
    const month = parseInt(key.slice(4, 6), 10); // Extract month from YYYYMM format
    if (!monthlyValues[month]) {
      monthlyValues[month] = [];
    }
    monthlyValues[month].push(value);
  });
  
  // Calculate average for each month
  const monthlyAverages: number[] = [];
  for (let i = 1; i <= 12; i++) {
    const values = monthlyValues[i] || [0];
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    monthlyAverages.push(avg);
  }
  
  return monthlyAverages;
}

/**
 * Calculate average value from multi-year data with fallback
 */
function calculateAverageValue(data: { [key: string]: number }, fallback: number): number {
  const values = Object.values(data);
  if (values.length === 0) return fallback;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}

/**
 * Reverse geocoding using OpenStreetMap Nominatim
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number
): Promise<{ address?: string; country?: string }> {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&accept-language=en`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Solar Investment Analysis Tool',
      },
    });
    
    if (!response.ok) {
      return {};
    }
    
    const data = await response.json();
    
    return {
      address: data.display_name,
      country: data.address?.country,
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return {};
  }
}